import React, { useMemo, useState } from 'react';

const ALL_CATEGORY_FILTER = 'all';
const UNCATEGORIZED_FILTER = '__uncategorized__';

const MovieEntryCard = ({ movie, categoryLabel = '', tone = 'default' }) => {
  const toneClassName =
    tone === 'highlight'
      ? 'border-blue-200 bg-gradient-to-br from-blue-50 via-white to-sky-50'
      : 'border-gray-200 bg-gray-50';

  return (
    <article
      className={`rounded-xl border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${toneClassName}`}
    >
      <p className="text-base font-semibold text-gray-900">{movie.title}</p>
      {categoryLabel ? (
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
          {categoryLabel}
        </p>
      ) : null}
      {movie.note ? (
        <p className="mt-3 whitespace-pre-line text-sm leading-6 text-gray-600">{movie.note}</p>
      ) : (
        <p className="mt-3 text-sm text-gray-400">Sem observacoes.</p>
      )}
    </article>
  );
};

const MoviesListsSection = ({
  favoriteSectionKeys,
  favoritesByCategory,
  favoritesLoadStatus,
  movieGroups,
  submissionsByColumn,
  formatCategoryLabel,
  getFavoriteSectionAccentClassName,
  getFavoriteSectionEmptyMessage,
}) => {
  const [favoriteSearchTerm, setFavoriteSearchTerm] = useState('');
  const [activeFavoriteCategoryKey, setActiveFavoriteCategoryKey] = useState(ALL_CATEGORY_FILTER);
  const [groupSearchById, setGroupSearchById] = useState({});
  const [activeGroupCategoryById, setActiveGroupCategoryById] = useState({});

  const normalizedFavoriteSearchTerm = favoriteSearchTerm.trim().toLowerCase();

  const filteredFavoritesByCategory = useMemo(() => {
    return favoriteSectionKeys.reduce((accumulator, categoryKey) => {
      const categoryMovies = favoritesByCategory[categoryKey] || [];

      if (!normalizedFavoriteSearchTerm) {
        accumulator[categoryKey] = categoryMovies;
        return accumulator;
      }

      accumulator[categoryKey] = categoryMovies.filter((movie) => {
        const searchableContent = [movie.title, movie.note, movie.category]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchableContent.includes(normalizedFavoriteSearchTerm);
      });
      return accumulator;
    }, {});
  }, [favoriteSectionKeys, favoritesByCategory, normalizedFavoriteSearchTerm]);

  const visibleFavoriteSectionKeys = useMemo(() => {
    const candidateKeys =
      activeFavoriteCategoryKey === ALL_CATEGORY_FILTER
        ? favoriteSectionKeys
        : favoriteSectionKeys.filter((categoryKey) => categoryKey === activeFavoriteCategoryKey);

    if (!normalizedFavoriteSearchTerm) {
      return candidateKeys;
    }

    return candidateKeys.filter((categoryKey) => {
      const categoryMovies = filteredFavoritesByCategory[categoryKey] || [];
      return categoryMovies.length > 0;
    });
  }, [
    activeFavoriteCategoryKey,
    favoriteSectionKeys,
    filteredFavoritesByCategory,
    normalizedFavoriteSearchTerm,
  ]);

  return (
    <section aria-labelledby="movies-lists-title" className="mb-20">
      <div className="max-w-2xl mx-auto text-center">
        <h2
          id="movies-lists-title"
          className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
        >
          Listas iniciais
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          As listas sao alimentadas por arquivos Markdown versionados em{' '}
          <code>src/data/movie-submissions</code>.
        </p>
      </div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <article className="bg-white rounded-2xl border border-gray-200 shadow-lg site-card overflow-hidden lg:col-span-2">
          <div className="p-6">
            <p className="text-sm font-semibold text-blue-600">Lista 01</p>
            <h3 className="mt-2 text-2xl font-semibold text-gray-900">Favoritos</h3>
            <p className="mt-3 text-gray-600">
              Cada commit da area administrativa gera um <code>.md</code> e vira item
              automaticamente aqui.
            </p>

            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 site-card-soft p-4">
              <label
                htmlFor="movies-favorites-search"
                className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500"
              >
                Buscar por titulo ou observacao
              </label>
              <input
                id="movies-favorites-search"
                type="search"
                value={favoriteSearchTerm}
                onChange={(event) => setFavoriteSearchTerm(event.target.value)}
                placeholder="Ex.: Blade Runner"
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveFavoriteCategoryKey(ALL_CATEGORY_FILTER)}
                  className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold transition ${
                    activeFavoriteCategoryKey === ALL_CATEGORY_FILTER
                      ? 'border-blue-300 bg-blue-100 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  Todas as categorias
                </button>
                {favoriteSectionKeys.map((categoryKey) => (
                  <button
                    key={categoryKey}
                    type="button"
                    onClick={() => setActiveFavoriteCategoryKey(categoryKey)}
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold transition ${
                      activeFavoriteCategoryKey === categoryKey
                        ? getFavoriteSectionAccentClassName(categoryKey)
                        : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {formatCategoryLabel(categoryKey)}
                  </button>
                ))}
              </div>
            </div>

            {favoritesLoadStatus === 'loading' ? (
              <p className="mt-6 text-sm text-gray-500">Carregando favoritos publicados...</p>
            ) : null}

            {favoritesLoadStatus === 'error' ? (
              <p className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Nao foi possivel carregar os favoritos dinamicos agora.
              </p>
            ) : null}

            <div className="mt-6 space-y-6">
              {visibleFavoriteSectionKeys.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                  Nenhum filme encontrado com os filtros atuais.
                </p>
              ) : null}

              {visibleFavoriteSectionKeys.map((categoryKey) => {
                const categoryTotal = (filteredFavoritesByCategory[categoryKey] || []).length;

                return (
                  <div key={categoryKey}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${getFavoriteSectionAccentClassName(categoryKey)}`}
                      >
                        {formatCategoryLabel(categoryKey)} ({categoryTotal})
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {(filteredFavoritesByCategory[categoryKey] || []).length > 0 ? (
                        (filteredFavoritesByCategory[categoryKey] || []).map((movie) => (
                          <MovieEntryCard
                            key={`${categoryKey}-${movie.id}`}
                            movie={movie}
                            categoryLabel={movie.category}
                            tone="highlight"
                          />
                        ))
                      ) : (
                        <p className="sm:col-span-2 rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                          {getFavoriteSectionEmptyMessage(categoryKey)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </article>

        <div className="space-y-8">
          {movieGroups.map((group) => {
            const dynamicItems = submissionsByColumn[group.column] || [];
            const mergedItems = [...dynamicItems, ...group.items];
            const groupSearchTerm = (groupSearchById[group.id] || '').trim().toLowerCase();
            const activeGroupCategoryKey = activeGroupCategoryById[group.id] || ALL_CATEGORY_FILTER;

            const groupCategoryKeys = Array.from(
              new Set(
                mergedItems.map((movie) => {
                  const normalizedCategory = (movie.category || '').trim();
                  return normalizedCategory || UNCATEGORIZED_FILTER;
                })
              )
            ).sort((left, right) => {
              const leftLabel = left === UNCATEGORIZED_FILTER ? 'Sem categoria' : left;
              const rightLabel = right === UNCATEGORIZED_FILTER ? 'Sem categoria' : right;
              return leftLabel.localeCompare(rightLabel, 'pt-BR');
            });

            const filteredItems = mergedItems.filter((movie) => {
              const movieCategoryKey = (movie.category || '').trim() || UNCATEGORIZED_FILTER;

              if (
                activeGroupCategoryKey !== ALL_CATEGORY_FILTER &&
                movieCategoryKey !== activeGroupCategoryKey
              ) {
                return false;
              }

              if (!groupSearchTerm) {
                return true;
              }

              const searchableContent = [movie.title, movie.note, movie.category]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return searchableContent.includes(groupSearchTerm);
            });

            return (
              <article
                key={group.id}
                className="bg-white rounded-2xl border border-gray-200 shadow-lg site-card overflow-hidden"
              >
                <div className="p-6">
                  <p className="text-sm font-semibold text-blue-600">{group.eyebrow}</p>
                  <h3 className="mt-2 text-2xl font-semibold text-gray-900">{group.title}</h3>
                  <p className="mt-3 text-gray-600">{group.description}</p>

                  <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 site-card-soft p-4">
                    <label
                      htmlFor={`movies-${group.id}-search`}
                      className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500"
                    >
                      Buscar nesta lista
                    </label>
                    <input
                      id={`movies-${group.id}-search`}
                      type="search"
                      value={groupSearchById[group.id] || ''}
                      onChange={(event) =>
                        setGroupSearchById((current) => ({
                          ...current,
                          [group.id]: event.target.value,
                        }))
                      }
                      placeholder={`Ex.: ${group.items[0]?.title || 'filme'}`}
                      className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setActiveGroupCategoryById((current) => ({
                            ...current,
                            [group.id]: ALL_CATEGORY_FILTER,
                          }))
                        }
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold transition ${
                          activeGroupCategoryKey === ALL_CATEGORY_FILTER
                            ? 'border-blue-300 bg-blue-100 text-blue-700'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        Todas
                      </button>

                      {groupCategoryKeys.map((categoryKey) => {
                        const label =
                          categoryKey === UNCATEGORIZED_FILTER ? 'Sem categoria' : categoryKey;

                        return (
                          <button
                            key={`${group.id}-${categoryKey}`}
                            type="button"
                            onClick={() =>
                              setActiveGroupCategoryById((current) => ({
                                ...current,
                                [group.id]: categoryKey,
                              }))
                            }
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold transition ${
                              activeGroupCategoryKey === categoryKey
                                ? 'border-slate-300 bg-slate-100 text-slate-700'
                                : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <p className="mt-4 text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">
                    {filteredItems.length} item(ns)
                  </p>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                    {filteredItems.map((movie) => (
                      <MovieEntryCard
                        key={movie.id || `${group.id}-${movie.title}`}
                        movie={movie}
                        categoryLabel={movie.category}
                      />
                    ))}

                    {filteredItems.length === 0 ? (
                      <p className="md:col-span-2 lg:col-span-1 xl:col-span-2 rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                        Nenhum filme encontrado nesta lista com os filtros atuais.
                      </p>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MoviesListsSection;
